import * as THREE from "three";
import { proxy } from "valtio";
import { type InputEvent, type InputHandler } from "./input-events";

const CLICK_TOLERANCE = 8;
export const DOUBLE_CLICK_TIME = 300;
const ZOOM_END_DELAY = 200;

export class InputManager {
    public state = proxy({
        dragActive: false,
        lastClickTime: 0,
    });

    private pointerDown: PointerEvent | null = null;

    private zoomEndTimeout: number | null = null;

    private lastClickPosition: THREE.Vector2 = new THREE.Vector2();

    private domElement: HTMLElement | null = null;

    private touches = new Map<
        number,
        {
            position: THREE.Vector2;
            previousPosition: THREE.Vector2;
            startPosition: THREE.Vector2;
        }
    >();

    private readonly handlers: InputHandler[];
    private detachListeners: (() => void) | null = null;

    constructor(...handlers: InputHandler[]) {
        this.handlers = handlers;
    }

    public dispatch(event: InputEvent) {
        for (const handler of this.handlers) {
            if (handler.handleInput(event)) {
                return true;
            }
        }
        return false;
    }

    private getScreenPosition(event: MouseEvent | PointerEvent | Touch): THREE.Vector2 {
        if (!this.domElement) {
            return new THREE.Vector2();
        }

        const rect = this.domElement.getBoundingClientRect();
        const x = ((event.clientX - rect.x) / this.domElement.clientWidth) * 2 - 1;
        const y = -((event.clientY - rect.y) / this.domElement.clientHeight) * 2 + 1;

        return new THREE.Vector2(x, y);
    }

    private handlePointerDown(event: PointerEvent) {
        if (event.currentTarget instanceof HTMLElement) {
            event.currentTarget.setPointerCapture(event.pointerId);
            event.currentTarget.focus();
        }

        event.preventDefault();
        this.pointerDown = event;

        const position = this.getScreenPosition(event);

        this.dispatch({
            type: "pointer-down",
            position: position,
            originalEvent: event,
        });
    }

    private handlePointerMove(event: PointerEvent) {
        let dragDispatched = false;

        if (this.pointerDown && !this.state.dragActive) {
            const dx = Math.abs(this.pointerDown.clientX - event.clientX);
            const dy = Math.abs(this.pointerDown.clientY - event.clientY);
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < CLICK_TOLERANCE) {
                return;
            }

            let position = this.getScreenPosition(this.pointerDown);

            if (this.touches.size === 2) {
                const [touch1, touch2] = this.touches.values();
                position = touch1.startPosition.clone().add(touch2.startPosition).multiplyScalar(0.5);
            }

            this.dispatch({
                originalEvent: this.pointerDown,
                type: "drag-start",
                position: position,
                touches: Array.from(this.touches.values()),
            });

            dragDispatched = true;
            this.state.dragActive = true;
            return;
        }

        if (this.state.dragActive && this.pointerDown) {
            let startPosition = this.getScreenPosition(this.pointerDown);
            let position = this.getScreenPosition(event);

            if (this.touches.size === 2) {
                const [touch1, touch2] = this.touches.values();
                startPosition = touch1.startPosition.clone().add(touch2.startPosition).multiplyScalar(0.5);
                position = touch1.position.clone().add(touch2.position).multiplyScalar(0.5);
            }

            this.dispatch({
                originalEvent: event,
                type: "drag",
                position: position,
                startPosition,
                touches: Array.from(this.touches.values()),
            });
            dragDispatched = true;
        }

        if (!dragDispatched) {
            const position = this.getScreenPosition(event);

            this.dispatch({
                originalEvent: event,
                position: position,
                type: "move",
            });
        }
    }

    private handlePointerUp(event: PointerEvent) {
        if (!this.pointerDown) {
            return;
        }

        if (event.currentTarget instanceof HTMLElement) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }

        const position = this.getScreenPosition(event);
        const now = performance.now();

        this.dispatch({
            type: "pointer-up",
            position: position,
            originalEvent: event,
        });

        if (this.state.dragActive) {
            const startPosition = this.getScreenPosition(this.pointerDown);
            const totalDelta = position.clone().sub(startPosition);

            this.dispatch({
                originalEvent: event,
                type: "drag-end",
                startPosition,
                position,
                totalDelta,
            });
        } else {
            const timeDiff = now - this.state.lastClickTime;
            const distance = position.distanceTo(this.lastClickPosition);

            if (timeDiff < DOUBLE_CLICK_TIME && distance < CLICK_TOLERANCE) {
                this.dispatch({
                    type: "double-click",
                    originalEvent: this.pointerDown,
                    position: position,
                });
                this.state.lastClickTime = 0;
            } else {
                this.dispatch({
                    type: "click",
                    originalEvent: this.pointerDown,
                    position: position,
                });
                this.state.lastClickTime = now;
                this.lastClickPosition.copy(position);
            }
        }

        this.pointerDown = null;
        this.state.dragActive = false;
    }

    private handlePointerCancel() {
        this.pointerDown = null;
        this.state.dragActive = false;
    }

    private handleWheel(event: WheelEvent) {
        event.preventDefault();

        const position = this.getScreenPosition(event);

        const probablyTrackpad = Math.abs(event.deltaY) < 50 && event.deltaMode === 0;
        let deltaY: number;

        if (probablyTrackpad) {
            deltaY = event.deltaY * 0.002;
        } else {
            deltaY = Math.sign(event.deltaY) * Math.min(Math.abs(event.deltaY) * 0.001, 0.2);
        }

        this.dispatch({
            originalEvent: event,
            delta: -deltaY,
            position: position,
            type: "zoom",
        });

        if (this.zoomEndTimeout !== null) {
            clearTimeout(this.zoomEndTimeout);
        }

        this.zoomEndTimeout = setTimeout(() => {
            this.zoomEndTimeout = null;
            this.dispatch({
                originalEvent: event,
                delta: -deltaY,
                position: position,
                type: "zoom-end",
            });
        }, ZOOM_END_DELAY);
    }

    private handleTouchStart(event: TouchEvent) {
        event.preventDefault();

        for (const touch of Array.from(event.changedTouches)) {
            const position = this.getScreenPosition(touch);

            this.touches.set(touch.identifier, {
                position: position.clone(),
                previousPosition: position.clone(),
                startPosition: position.clone(),
            });
        }
    }

    private handleTouchMove(event: TouchEvent) {
        event.preventDefault();

        for (const touch of Array.from(event.changedTouches)) {
            const position = this.getScreenPosition(touch);
            const touchData = this.touches.get(touch.identifier);

            if (touchData) {
                touchData.previousPosition.copy(touchData.position);
                touchData.position.copy(position);
            }
        }
    }

    private handleTouchEnd(event: TouchEvent) {
        event.preventDefault();

        for (const touch of Array.from(event.changedTouches)) {
            this.touches.delete(touch.identifier);
        }
    }

    private handleKeyDown(event: KeyboardEvent) {
        this.dispatch({
            originalEvent: event,
            type: "key-down",
            key: event.key,
            code: event.code,
        });
    }

    private handleKeyUp(event: KeyboardEvent) {
        this.dispatch({
            originalEvent: event,
            type: "key-up",
            key: event.key,
            code: event.code,
        });
    }

    public attach(domElement: HTMLElement) {
        this.detach();

        this.domElement = domElement;

        const onContextMenu = (e: Event) => e.preventDefault();
        const onPointerDown = (e: PointerEvent) => this.handlePointerDown(e);
        const onPointerMove = (e: PointerEvent) => this.handlePointerMove(e);
        const onPointerUp = (e: PointerEvent) => this.handlePointerUp(e);
        const onPointerCancel = () => this.handlePointerCancel();
        const onWheel = (e: WheelEvent) => this.handleWheel(e);
        const onTouchStart = (e: TouchEvent) => this.handleTouchStart(e);
        const onTouchMove = (e: TouchEvent) => this.handleTouchMove(e);
        const onTouchEnd = (e: TouchEvent) => this.handleTouchEnd(e);
        const onKeyDown = (e: KeyboardEvent) => this.handleKeyDown(e);
        const onKeyUp = (e: KeyboardEvent) => this.handleKeyUp(e);

        domElement.addEventListener("contextmenu", onContextMenu);
        domElement.addEventListener("pointerdown", onPointerDown);
        domElement.addEventListener("pointermove", onPointerMove);
        domElement.addEventListener("pointerup", onPointerUp);
        domElement.addEventListener("pointercancel", onPointerCancel);
        domElement.addEventListener("wheel", onWheel);
        domElement.addEventListener("touchstart", onTouchStart);
        domElement.addEventListener("touchmove", onTouchMove);
        domElement.addEventListener("touchend", onTouchEnd);
        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("keyup", onKeyUp);

        this.detachListeners = () => {
            domElement.removeEventListener("contextmenu", onContextMenu);
            domElement.removeEventListener("pointerdown", onPointerDown);
            domElement.removeEventListener("pointermove", onPointerMove);
            domElement.removeEventListener("pointerup", onPointerUp);
            domElement.removeEventListener("pointercancel", onPointerCancel);
            domElement.removeEventListener("wheel", onWheel);
            domElement.removeEventListener("touchstart", onTouchStart);
            domElement.removeEventListener("touchmove", onTouchMove);
            domElement.removeEventListener("touchend", onTouchEnd);
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("keyup", onKeyUp);
        };
    }

    public detach() {
        this.detachListeners?.();
        this.detachListeners = null;
        this.domElement = null;
    }
}
